import type { Master } from "@/api/masterService";
import CloseButton from "@/components/buttons/CloseButton";
import {
  useGetOEMPartOtherProcesses,
  useGetSparePartOtherProcesses,
} from "@/hooks/masterHook";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { InformationModal } from "@/styles/globalstyles";
import { ErrorAlert } from "@/utils/alert";
import { getAxiosErrorMessage } from "@/utils/healper";
import { Modal, Box, Typography, Divider } from "@mui/material";
import { useState } from "react";
import EditableProcessList from "../breakdown-process/EditableProcessList";
import {
  useCreateBreakdownExchangeRateOEMProcess,
  useCreateBreakdownExchangeRateSpareProcess,
  useDeleteBreakdownExchangeRateOEMProcess,
  useDeleteBreakdownExchangeRateSpareProcess,
  useGetBreakdownExchangeRateProcess,
  useUpdateBreakdownExchangeRateOEMProcess,
  useUpdateBreakdownExchangeRateSpareProcess,
} from "@/hooks/breakdownExchangeRateHooks";

interface ManageExchangeRateOtherProcessModalProps {
  open: boolean;
  handleClose: () => void;
  supplierCode?: string;
  canEdit: boolean;
}

const ManageExchangeRateOtherProcessModal = ({
  open,
  handleClose,
  supplierCode,
  canEdit,
}: ManageExchangeRateOtherProcessModalProps) => {
  const [disabled, setDisabled] = useState(false);

  const {
    data: masterOEMOtherProcesses,
    isLoading: isLoadingMasterOEMOtherProcesses,
    isError: isErrorMasterOEMOtherProcesses,
  } = useGetOEMPartOtherProcesses(
    { skip: 0, limit: 1000, active_only: true },
    true,
  );

  const {
    data: masterSpareOtherProcesses,
    isLoading: isLoadingMasterSpareOtherProcesses,
    isError: isErrorMasterSpareOtherProcesses,
  } = useGetSparePartOtherProcesses(
    { skip: 0, limit: 1000, active_only: true },
    true,
  );

  const {
    data: exchangeRateProcess,
    isLoading: isLoadingExchangeRateProcess,
    isError: isErrorExchangeRateProcess,
  } = useGetBreakdownExchangeRateProcess(supplierCode || "");

  const {
    mutate: createOEMOtherProcess,
    isPending: isCreatingOEMOtherProcess,
  } = useCreateBreakdownExchangeRateOEMProcess();

  const {
    mutate: createSpareOtherProcess,
    isPending: isCreatingSpareOtherProcess,
  } = useCreateBreakdownExchangeRateSpareProcess();

  const {
    mutate: updateOEMOtherProcess,
    isPending: isUpdatingOEMOtherProcess,
  } = useUpdateBreakdownExchangeRateOEMProcess();

  const {
    mutate: updateSpareOtherProcess,
    isPending: isUpdatingSpareOtherProcess,
  } = useUpdateBreakdownExchangeRateSpareProcess();

  const {
    mutate: deleteOEMOtherProcess,
    isPending: isDeletingOEMOtherProcess,
  } = useDeleteBreakdownExchangeRateOEMProcess();

  const {
    mutate: deleteSpareOtherProcess,
    isPending: isDeletingSpareOtherProcess,
  } = useDeleteBreakdownExchangeRateSpareProcess();

  useQueryHandler({
    isFetching:
      isLoadingExchangeRateProcess ||
      isCreatingOEMOtherProcess ||
      isCreatingSpareOtherProcess ||
      isUpdatingOEMOtherProcess ||
      isUpdatingSpareOtherProcess ||
      isDeletingOEMOtherProcess ||
      isDeletingSpareOtherProcess ||
      isLoadingMasterOEMOtherProcesses ||
      isLoadingMasterSpareOtherProcesses,
    isError:
      isErrorExchangeRateProcess ||
      isErrorMasterOEMOtherProcesses ||
      isErrorMasterSpareOtherProcesses,
  });

  const onClose = () => {
    handleClose();
    setDisabled(false);
  };

  const handleAddOEMOtherProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createOEMOtherProcess(
      {
        supplier_code: supplierCode || "",
        ms_process_id: process.master_process_id,
      },
      {
        onError: (error) => {
          console.error("Error creating OEM other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleAddSpareOtherProcess = async (process: {
    name: string;
    master_process_id: number;
  }) => {
    createSpareOtherProcess(
      {
        supplier_code: supplierCode || "",
        ms_process_id: process.master_process_id,
      },
      {
        onError: (error) => {
          console.error("Error creating spare other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditOEMOtherProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updateOEMOtherProcess(
      {
        id: process.id,
        supplier_code: supplierCode || "",
        ms_process_id: process.master_process_id,
      },
      {
        onError: (error) => {
          console.error("Error updating OEM other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleEditSpareOtherProcess = async (process: {
    id: number;
    name: string;
    master_process_id: number;
  }) => {
    updateSpareOtherProcess(
      {
        id: process.id,
        supplier_code: supplierCode || "",
        ms_process_id: process.master_process_id,
      },
      {
        onError: (error) => {
          console.error("Error updating spare other process:", error);
          ErrorAlert("Error", getAxiosErrorMessage(error));
        },
      },
    );
  };

  const handleDeleteOEMOtherProcess = async (processId: number) => {
    deleteOEMOtherProcess(processId, {
      onError: (error) => {
        console.error("Error deleting OEM other process:", error);
        ErrorAlert("Error", getAxiosErrorMessage(error));
      },
    });
  };

  const handleDeleteSpareOtherProcess = async (processId: number) => {
    deleteSpareOtherProcess(processId, {
      onError: (error) => {
        console.error("Error deleting spare other process:", error);
        ErrorAlert("Error", getAxiosErrorMessage(error));
      },
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          ...InformationModal,
          minHeight: "400px",
          maxHeight: "90vh",
          width: "70vw",
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="h2">{"จัดการ Part Other Process"}</Typography>
        <Divider sx={{ my: 1 }} />
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} p={1} pt={2}>
          <EditableProcessList
            title="OEM Part Other Process"
            data={
              exchangeRateProcess?.oem_other_process?.map((process) => ({
                id: process.id,
                name: process.name,
                master_process_id: process.ms_process_id || 0,
              })) || []
            }
            options={
              masterOEMOtherProcesses?.map((process: Master) => ({
                label: process.name,
                value: process.id,
              })) || []
            }
            handleAddProcess={async (process) => {
              handleAddOEMOtherProcess(process);
            }}
            handleEditProcess={async (process) => {
              handleEditOEMOtherProcess(process);
            }}
            handleDeleteProcess={async (processId) => {
              handleDeleteOEMOtherProcess(processId);
            }}
            disabled={disabled || !canEdit}
            setDisabled={setDisabled}
          />
          <EditableProcessList
            title="Spare Part Other Process"
            data={
              exchangeRateProcess?.spare_other_process?.map((process) => ({
                id: process.id,
                name: process.name,
                master_process_id: process.ms_process_id || 0,
              })) || []
            }
            options={
              masterSpareOtherProcesses?.map((process: Master) => ({
                label: process.name,
                value: process.id,
              })) || []
            }
            handleAddProcess={async (process) => {
              handleAddSpareOtherProcess(process);
            }}
            handleEditProcess={async (process) => {
              handleEditSpareOtherProcess(process);
            }}
            handleDeleteProcess={async (processId) => {
              handleDeleteSpareOtherProcess(processId);
            }}
            disabled={disabled || !canEdit}
            setDisabled={setDisabled}
          />
        </Box>
      </Box>
    </Modal>
  );
};

export default ManageExchangeRateOtherProcessModal;
